import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  MapPin, Calendar, Users, Clock, ChevronDown, ChevronUp,
  Utensils, Home, Wine, Camera, Bike, Heart, RefreshCw,
  AlertCircle, CheckCircle, Play, Star, MessageSquare,
  Lock, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  draft: { 
    label: 'Bozza', 
    color: 'bg-amber-100 text-amber-700', 
    icon: Clock,
    description: 'Il tuo viaggio è in costruzione'
  },
  confirmed: { 
    label: 'Confermato', 
    color: 'bg-green-100 text-green-700', 
    icon: CheckCircle,
    description: 'Il tuo viaggio è confermato'
  },
  active: { 
    label: 'In Corso', 
    color: 'bg-blue-100 text-blue-700', 
    icon: Play,
    description: 'Buon viaggio!'
  },
  completed: { 
    label: 'Completato', 
    color: 'bg-purple-100 text-purple-700', 
    icon: Star,
    description: 'Com\'è andato il tuo viaggio?'
  },
  cancelled: { 
    label: 'Annullato', 
    color: 'bg-gray-100 text-gray-600', 
    icon: AlertCircle,
    description: 'Viaggio annullato'
  }
};

// Type icons
const TYPE_ICONS = {
  accommodation: Home,
  restaurant: Utensils,
  winery: Wine,
  experience: Camera,
  activity: Bike,
  wellness: Heart,
  guide: Users,
  transport: MapPin
};

/**
 * TravelPassPage - Pagina pubblica del Travel Pass
 * 
 * Fase 1: Solo consultazione
 * - Visualizza stato, date, gruppo
 * - Lista esperienze per giorno
 * - Mappa delle tappe
 * - Nessuna azione operativa
 */
export default function TravelPassPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  
  const [passData, setPassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});

  const loadPass = useCallback(async () => {
    if (!code) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/pass/${code}`);
      
      if (!res.ok) {
        if (res.status === 404) {
          setError('Travel Pass non trovato');
        } else {
          setError('Errore nel caricamento');
        }
        return;
      }
      
      const data = await res.json();
      setPassData(data);
      
      // Expand first day by default
      if (data.itinerary_items?.length > 0) {
        const firstDay = data.itinerary_items[0]?.day;
        if (firstDay) {
          setExpandedDays({ [firstDay]: true });
        }
      }
    } catch (err) {
      console.error('Failed to load pass:', err);
      setError('Errore di connessione');
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    loadPass();
  }, [loadPass]);

  const toggleDay = (day) => {
    setExpandedDays(prev => ({ ...prev, [day]: !prev[day] }));
  };

  // Group items by day
  const itemsByDay = {};
  (passData?.itinerary_items || []).forEach(item => {
    const day = item.day || 0;
    if (!itemsByDay[day]) {
      itemsByDay[day] = [];
    }
    itemsByDay[day].push(item);
  });

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-umbria-cream to-white flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-umbria-olive mx-auto mb-4" />
          <p className="text-umbria-charcoal">Caricamento del tuo viaggio...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-umbria-cream to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-umbria-charcoal mb-2">
              {error}
            </h2>
            <p className="text-gray-500 mb-6">
              Il link potrebbe essere errato o il viaggio potrebbe essere stato rimosso.
            </p>
            <Button onClick={() => navigate('/')}>
              Torna alla Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!passData) return null;

  const statusConfig = STATUS_CONFIG[passData.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-umbria-cream to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-umbria-stone/20 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-umbria-olive to-umbria-terracotta flex items-center justify-center">
                <span className="text-white font-bold text-sm">FU</span>
              </div>
              <div>
                <p className="text-xs text-gray-500">FEEL UMBRIA</p>
                <p className="font-medium text-umbria-charcoal text-sm">Travel Pass</p>
              </div>
            </div>
            
            <Badge className={statusConfig.color}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusConfig.label}
            </Badge>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        
        {/* Entry Point to Il Mio Viaggio */}
        <Card className="bg-gradient-to-r from-umbria-olive/10 via-umbria-sage/5 to-transparent border-umbria-olive/20">
          <CardContent className="py-5">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-umbria-olive/20 flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-umbria-olive" />
                </div>
                <div>
                  <h3 className="font-semibold text-umbria-charcoal">Il Mio Viaggio</h3>
                  <p className="text-sm text-umbria-muted">
                    Esplora il tuo itinerario completo con mappa e timeline
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate(`/il-mio-viaggio/${code}`)}
                className="bg-umbria-olive hover:bg-umbria-olive/90 gap-2"
                data-testid="go-to-viaggio"
              >
                Vai al viaggio
                <ArrowRight size={16} />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Trip Header */}
        <section className="text-center py-8">
          <h1 className="text-3xl md:text-4xl font-serif text-umbria-charcoal mb-3">
            {passData.title || 'Il tuo viaggio in Umbria'}
          </h1>
          <p className="text-umbria-olive/80">
            {statusConfig.description}
          </p>
        </section>

        {/* Trip Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Dates */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-umbria-olive/10 rounded-lg">
                  <Calendar className="h-5 w-5 text-umbria-olive" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Date</p>
                  <p className="font-medium text-umbria-charcoal">
                    {passData.trip_dates?.start && passData.trip_dates?.end ? (
                      `${formatDate(passData.trip_dates.start)} - ${formatDate(passData.trip_dates.end)}`
                    ) : passData.trip_dates?.period_type || (
                      'Da definire'
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Group */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-umbria-olive/10 rounded-lg">
                  <Users className="h-5 w-5 text-umbria-olive" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Gruppo</p>
                  <p className="font-medium text-umbria-charcoal">
                    {formatGroup(passData.group)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Base */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-umbria-olive/10 rounded-lg">
                  <MapPin className="h-5 w-5 text-umbria-olive" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Base</p>
                  <p className="font-medium text-umbria-charcoal">
                    {passData.base_location || 'Umbria'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map Placeholder */}
        {passData.map_markers?.length > 0 && (
          <Card className="overflow-hidden">
            <div className="h-64 bg-gradient-to-br from-umbria-stone/20 to-umbria-cream flex items-center justify-center">
              <div className="text-center">
                <MapPin className="h-12 w-12 text-umbria-olive/40 mx-auto mb-2" />
                <p className="text-umbria-charcoal/60">
                  Mappa con {passData.map_markers.length} tappe
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Integrazione Mapbox in arrivo
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Itinerary */}
        <section>
          <h2 className="text-xl font-semibold text-umbria-charcoal mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-umbria-olive" />
            Il Tuo Itinerario
          </h2>

          {Object.keys(itemsByDay).length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">L'itinerario è ancora in costruzione</p>
                <p className="text-sm text-gray-400 mt-1">
                  Chiara sta preparando le tue esperienze
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {Object.entries(itemsByDay).sort(([a], [b]) => Number(a) - Number(b)).map(([day, items]) => {
                const isExpanded = expandedDays[day];
                const firstItem = items[0];
                
                return (
                  <Card key={day} className="overflow-hidden">
                    {/* Day Header - Always visible */}
                    <button
                      onClick={() => toggleDay(day)}
                      className="w-full px-4 py-3 flex items-center justify-between bg-umbria-olive/5 hover:bg-umbria-olive/10 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-umbria-olive/20 flex items-center justify-center">
                          <span className="font-bold text-umbria-olive">{day}</span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-umbria-charcoal">
                            Giorno {day}
                          </p>
                          <p className="text-xs text-gray-500">
                            {firstItem?.date_hint || ''} • {items.length} esperienze
                          </p>
                        </div>
                      </div>
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      )}
                    </button>

                    {/* Day Content - Collapsible */}
                    {isExpanded && (
                      <CardContent className="pt-4 space-y-3">
                        {items.map((item, idx) => {
                          const TypeIcon = TYPE_ICONS[item.type] || Camera;
                          
                          return (
                            <div 
                              key={item.id || idx}
                              className={`p-4 rounded-lg border transition-all ${
                                item.is_main 
                                  ? 'bg-umbria-cream/50 border-umbria-olive/20' 
                                  : 'bg-white border-gray-100'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                {/* Icon */}
                                <div className={`p-2 rounded-lg ${
                                  item.is_main 
                                    ? 'bg-umbria-olive/20' 
                                    : 'bg-gray-100'
                                }`}>
                                  <TypeIcon className={`h-5 w-5 ${
                                    item.is_main 
                                      ? 'text-umbria-olive' 
                                      : 'text-gray-500'
                                  }`} />
                                </div>
                                
                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div>
                                      <h4 className="font-medium text-umbria-charcoal">
                                        {item.title}
                                      </h4>
                                      {item.area && (
                                        <p className="text-sm text-gray-500 flex items-center gap-1">
                                          <MapPin className="h-3 w-3" />
                                          {item.town || item.area}
                                        </p>
                                      )}
                                    </div>
                                    
                                    <div className="text-right flex-shrink-0">
                                      {item.time && (
                                        <Badge variant="secondary" className="text-xs">
                                          <Clock className="h-3 w-3 mr-1" />
                                          {item.time}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Why suggested */}
                                  {item.why_suggested && (
                                    <p className="text-sm text-umbria-olive/80 mt-2 italic">
                                      "{item.why_suggested}"
                                    </p>
                                  )}
                                  
                                  {/* Duration */}
                                  {item.duration && (
                                    <p className="text-xs text-gray-400 mt-1">
                                      Durata: {item.duration}
                                    </p>
                                  )}
                                  
                                  {/* Status badge */}
                                  <div className="mt-2">
                                    <Badge 
                                      variant="outline"
                                      className={
                                        item.status === 'confirmed' 
                                          ? 'border-green-300 text-green-700' 
                                          : 'border-amber-300 text-amber-700'
                                      }
                                    >
                                      {item.status === 'confirmed' ? 'Confermato' : 'Proposto'}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* Actions - Fase 2 teaser */}
        <Card className="bg-gradient-to-r from-umbria-olive/5 to-umbria-terracotta/5 border-umbria-olive/20">
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white rounded-full shadow-sm">
                <Lock className="h-6 w-6 text-umbria-olive" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-umbria-charcoal">
                  Vuoi modificare il viaggio?
                </h3>
                <p className="text-sm text-gray-500">
                  Per aggiungere esperienze o modificare l'itinerario, 
                  ti invieremo un link di verifica alla tua email.
                </p>
              </div>
              <Button variant="outline" disabled className="gap-2">
                <MessageSquare className="h-4 w-4" />
                Prossimamente
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <footer className="text-center py-8 text-sm text-gray-400">
          <p>Travel Pass: <code className="bg-gray-100 px-2 py-1 rounded">{code}</code></p>
          <p className="mt-2">
            Email: {passData.client_email_masked}
          </p>
        </footer>
      </main>
    </div>
  );
}

// Helper functions
function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'short' 
    });
  } catch {
    return dateStr;
  }
}

function formatGroup(group) {
  if (!group) return '2 persone';
  
  const parts = [];
  const adults = group.adults || 2;
  const children = group.children || 0;
  
  parts.push(`${adults} adult${adults > 1 ? 'i' : 'o'}`);
  
  if (children > 0) {
    parts.push(`${children} bambin${children > 1 ? 'i' : 'o'}`);
  }
  
  if (group.party_type) {
    const typeLabels = {
      couple: 'Coppia',
      family: 'Famiglia',
      friends: 'Amici',
      solo: 'Solo'
    };
    const typeLabel = typeLabels[group.party_type];
    if (typeLabel) {
      return `${typeLabel} (${parts.join(', ')})`;
    }
  }
  
  return parts.join(', ');
}
